import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Tab,
} from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  dialogState,
  formTemplateState,
  layoutSelector,
  projectState,
} from 'state';
import { FormTemplateStatus, LayoutItemStyle } from 'api/models/FormTemplate';
import { produce } from 'immer';
import { TabContext, TabList } from '@mui/lab';
import { Input, VisibilityOff } from '@mui/icons-material';
import LayoutConfigTab from './components/LayoutConfigTab';
import { useState } from 'react';
import LayoutVisibilityTab from './components/LayoutVisibilityTab';
import { ConditionGroup } from 'api/models/FieldTemplate';
import { set } from 'object-path';

export type EditLayoutItemFormikValues = LayoutItemStyle & {
  isVisible?: ConditionGroup;
};

export function EditLayoutItemDialog() {
  const [{ data }, closeDialog] = useRecoilState(dialogState);
  const { layoutItem: item, path } = data;
  const { t } = useTranslation();
  const [layout, setLayout] = useRecoilState(layoutSelector);
  const [activeTab, setActiveTab] = useState('1');
  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };
  const { selectedProject } = useRecoilValue(projectState);
  const formTemplate = useRecoilValue(formTemplateState);
  const disabled = formTemplate?.status === FormTemplateStatus.Published;

  const formik = useFormik<EditLayoutItemFormikValues>({
    initialValues: {
      backgroundColor: item.layoutOptions?.style?.backgroundColor,
      marginTop: item.layoutOptions?.style?.marginTop?.replaceAll('px', ''),
      marginBottom: item.layoutOptions?.style?.marginBottom?.replaceAll(
        'px',
        '',
      ),
      marginLeft: item.layoutOptions?.style?.marginLeft?.replaceAll('px', ''),
      marginRight: item.layoutOptions?.style?.marginRight?.replaceAll('px', ''),
      paddingTop: item.layoutOptions?.style?.paddingTop?.replaceAll('px', ''),
      paddingBottom: item.layoutOptions?.style?.paddingBottom?.replaceAll(
        'px',
        '',
      ),
      paddingLeft: item.layoutOptions?.style?.paddingLeft?.replaceAll('px', ''),
      paddingRight: item.layoutOptions?.style?.paddingRight?.replaceAll(
        'px',
        '',
      ),
      isVisible: item.layoutOptions?.isVisible,
      isRepeatable: item.layoutOptions?.isRepeatable ?? false,
    },
    onSubmit: async (values, { resetForm }) => {
      const items = produce(layout, draft => {
        const styles = {
          backgroundColor: values.backgroundColor,
          marginTop: values.marginTop ? `${values.marginTop}px` : undefined,
          marginBottom: values.marginBottom
            ? `${values.marginBottom}px`
            : undefined,
          marginLeft: values.marginLeft ? `${values.marginLeft}px` : undefined,
          marginRight: values.marginRight
            ? `${values.marginRight}px`
            : undefined,
          paddingTop: values.paddingTop ? `${values.paddingTop}px` : undefined,
          paddingBottom: values.paddingBottom
            ? `${values.paddingBottom}px`
            : undefined,
          paddingLeft: values.paddingLeft
            ? `${values.paddingLeft}px`
            : undefined,
          paddingRight: values.paddingRight
            ? `${values.paddingRight}px`
            : undefined,
        };
        set(draft, `${path}.layoutOptions.style`, styles);
        set(draft, `${path}.layoutOptions.isVisible`, values.isVisible);
        set(draft, `${path}.layoutOptions.isRepeatable`, values.isRepeatable);
      });
      setLayout(items);

      resetForm();

      closeDialog({
        isOpen: false,
      });
    },
  });

  return (
    <>
      <DialogTitle>{t('dialogs.editLayoutField.title')}</DialogTitle>
      <DialogContent>
        <form>
          <TabContext value={activeTab}>
            <TabList onChange={handleTabChange}>
              <Tab
                label={t('dialogs.addField.configTab')}
                value="1"
                icon={<Input />}
              />
              <Tab
                label={t('dialogs.addField.visibilityTab')}
                value="2"
                icon={<VisibilityOff />}
              />
            </TabList>
            <LayoutConfigTab
              formik={formik}
              disabled={disabled}
              isRepeatingFieldsEnabled={
                selectedProject?.configuration?.repeatingFieldsEnabled ?? false
              }
            />
            <LayoutVisibilityTab formik={formik} disabled={disabled} />
          </TabContext>
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            closeDialog({
              isOpen: false,
            });
          }}
          color="secondary"
          variant="contained"
          sx={{ mb: 1 }}
        >
          {t('general.cancel')}
        </Button>
        <Button
          disabled={!formik.isValid || disabled}
          type="submit"
          color="primary"
          variant="contained"
          onClick={formik.submitForm}
          sx={{ mr: 2, mb: 1 }}
        >
          {t('general.save')}
        </Button>
      </DialogActions>
    </>
  );
}
